import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { fetchWithAuth } from "../utils/api";
import UserLink from "./UserLink";

function ChatBox({ diveIntentionId, currentUser, organizer }) {
  const { t, i18n } = useTranslation();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const apiUrl = process.env.REACT_APP_API_URL;
  const chatEndRef = useRef(null);
  const [chatParticipants, setChatParticipants] = useState([]);


  // --- Scroll auto vers le bas
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // --- Récupération des messages
  const fetchMessages = async () => {
    try {
      const response = await fetchWithAuth(`${apiUrl}/chat/${diveIntentionId}`);
      const data = await response.json();
      setMessages(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Erreur lors de la récupération des messages", error);
    }
  };

  useEffect(() => {
    if (!diveIntentionId) return;
    fetchWithAuth(`${apiUrl}/dive-participants/${diveIntentionId}`)
      .then((res) => res.json())
      .then((data) => {
        const parts = Array.isArray(data) ? data.map((p) => p.users).filter(Boolean) : [];
        // L'organisateur n'est pas dans dive_participants : on l'ajoute en tête, sans doublon.
        const merged = organizer
          ? [organizer, ...parts.filter((u) => u && u.id !== organizer.id)]
          : parts;
        setChatParticipants(merged);
      })
      .catch((err) =>
        console.error("Erreur lors du chargement des participants :", err)
      );
  }, [diveIntentionId, apiUrl, organizer]);

  useEffect(() => {
    if (diveIntentionId) {
      fetchMessages();
      const interval = setInterval(fetchMessages, 3000);
      return () => clearInterval(interval);
    }
  }, [diveIntentionId]);

  // --- Envoi d'un message
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;
    setLoading(true);
    try {
      const response = await fetchWithAuth(`${apiUrl}/chat`, {
        method: "POST",
        body: JSON.stringify({
          dive_intention_id: diveIntentionId,
          message: newMessage.trim(),
        }),
      });
      if (!response.ok) throw new Error("Erreur lors de l'envoi du message");
      setNewMessage("");
      fetchMessages();
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // --- Formatage de l'heure
  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString(i18n.language, { hour: "2-digit", minute: "2-digit" });
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "min(420px, 70dvh)",
        width: "100%",
        background: "#f9fafb",
        borderRadius: "12px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        overflow: "hidden",
        position: "relative",
      }}
    >
      {/* --- En-tête --- */}
      <div
        style={{
          background: "var(--primary-blue)",
          color: "white",
          padding: "10px 14px",
          fontWeight: "600",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <span>{t('chat.title')}</span>
          <select
            style={{
              background: "white",
              color: "var(--primary-blue)",
              border: "none",
              borderRadius: "6px",
              fontSize: "13px",
              padding: "3px 6px",
              cursor: "pointer",
            }}
            defaultValue=""
          >
            <option value="">{t('chat.participants')}</option>
            {chatParticipants.filter(Boolean).map((u, i) => (
              <option key={u.id || i} value={u.id || ""}>
                {u.first_name} {u.last_name}
              </option>
            ))}
          </select>
        </div>

        <button
          onClick={() => {
            if (typeof window.closeChat === "function") window.closeChat();
          }}
          style={{
            background: "transparent",
            color: "white",
            border: "none",
            fontSize: "18px",
            cursor: "pointer",
            marginLeft: "auto",
          }}
        >
          ✕
        </button>
      </div>

      {/* --- Zone des messages --- */}
      <div
        style={{
          flex: 1,
          padding: "12px",
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          gap: "10px",
        }}
      >
        {messages.length === 0 ? (
          <p style={{ color: "#777", textAlign: "center", marginTop: "40px" }}>
            {t('chat.noMessages')}
          </p>
        ) : (
          messages.map((msg) => {
            const isUser = msg.user_id === currentUser?.id;
            return (
              <div
                key={msg.id}
                style={{
                  alignSelf: isUser ? "flex-end" : "flex-start",
                  background: isUser ? "var(--primary-blue)" : "#e3f2fd",
                  color: isUser ? "white" : "#222",
                  padding: "10px 14px",
                  borderRadius: "16px",
                  maxWidth: "75%",
                  wordBreak: "break-word",
                  boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                }}
              >
                {!isUser && (
                  <div
                    style={{
                      fontSize: "12px",
                      fontWeight: "600",
                      marginBottom: "4px",
                    }}
                  >
                    {msg.user
                      ? <UserLink id={msg.user_id} firstName={msg.user.first_name} lastName={msg.user.last_name} />
                      : t('chat.user')}
                  </div>
                )}
                <div style={{ fontSize: "14px" }}>{msg.message}</div>
                <div
                  style={{
                    fontSize: "11px",
                    opacity: 0.7,
                    marginTop: "4px",
                    textAlign: "right",
                  }}
                >
                  {msg.created_at
                    ? new Date(msg.created_at).toLocaleTimeString(i18n.language, {
                      hour: "2-digit",
                      minute: "2-digit",
                    })
                    : ""}
                </div>
              </div>
            );
          })
        )}
        <div ref={chatEndRef} />
      </div>

      {/* --- Champ de saisie --- */}
      <form
        onSubmit={handleSubmit}
        style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          borderTop: "1px solid #ddd",
          padding: "8px 10px",
          background: "#fff",
        }}
      >
        <input
          type="text"
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          placeholder={t('chat.placeholder')}
          style={{
            flex: 1,
            padding: "8px 12px",
            border: "1px solid #ccc",
            borderRadius: "20px",
            fontSize: "14px",
            outline: "none",
          }}
        />
        <button
          type="submit"
          disabled={loading}
          style={{
            width: "38px",
            height: "38px",
            background: "var(--primary-blue)",
            border: "none",
            borderRadius: "50%",
            color: "white",
            fontSize: "16px",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            transition: "background 0.2s",
          }}
          title={t('chat.sendButton')}
        >
          ➤
        </button>
      </form>
    </div>
  );

}

export default ChatBox;
